import { Aviso } from "@/components/Aviso"
import { Carregando } from "@/components/Carregando"
import { Marca } from "@/components/Marca"
import { Navegacao } from "@/components/Navegacao"
import { ApiError, type FalhaDoPortal } from "@/lib/api"
import { getToken } from "@/lib/session"
import { formatCurrency, formatPercent } from "@/lib/utils"
import { useParceiro } from "@/parceiros/contexto"
import { useQuery } from "@tanstack/react-query"
import { CabecalhoEmpresa } from "./CabecalhoEmpresa"
import { MargemDaFolha } from "./MargemDaFolha"
import { fetchVisao } from "./api"
import { Barras, Kpi, Secao } from "./blocos"
import type { Tom } from "./tons"

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"]

const TAXA = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })

const mesCurto = (chave: string) => {
    const [ano, mes] = chave.split("-")
    return `${MESES[Number(mes) - 1] ?? mes}/${ano?.slice(2) ?? ""}`
}

// Faixas de margem: pouca margem é aperto na folha, muita margem é folga. A faixa "<0,01" é quem
// não tem margem nenhuma — o motivo mais comum de um pedido não virar nada.
const tomDaMargem = (indice: number, total: number): Tom => {
    if (indice === 0) return "stop"
    if (indice <= Math.floor(total / 3)) return "off"
    return "ok"
}

export const VisaoPage = () => {
    const parceiro = useParceiro()
    // Mesma regra da tela de repasse: o token é capturado antes do render e vive na aba. Sem ele,
    // quem chegou aqui copiou o endereço da barra — dizer "seu link expirou" seria mentira.
    const semToken = !getToken()

    const { data, isPending, error, refetch } = useQuery({
        queryKey: ["visao"],
        queryFn: fetchVisao,
        enabled: !semToken,
        retry: (tentativas, erro) =>
            erro instanceof ApiError &&
            (erro.falha === "rede" || erro.falha === "servidor") &&
            tentativas < 2,
    })

    if (semToken) {
        return (
            <Aviso titulo="Abra esta página pelo link do e-mail" ocultarSuporte>
                <p>
                    O endereço sozinho não dá acesso: quem abre a página é o link que enviamos para
                    você. Procure o e-mail mais recente {parceiro.doParceiro}.
                </p>
            </Aviso>
        )
    }

    if (isPending) return <Carregando />

    if (error) {
        const falha: FalhaDoPortal = error instanceof ApiError ? error.falha : "servidor"
        const tentarDeNovo = { rotulo: "Tentar de novo", onClick: () => void refetch() }

        if (falha === "link") {
            return (
                <Aviso titulo="Este link não vale mais">
                    <p>
                        Por segurança o acesso expira depois de alguns dias, e o seu já passou da
                        validade.
                    </p>
                    <p>
                        Abra o <b>e-mail mais recente</b> {parceiro.doParceiro} e clique de novo no
                        link de lá.
                    </p>
                </Aviso>
            )
        }

        if (falha === "rede") {
            return (
                <Aviso titulo="Não conseguimos conectar" acao={tentarDeNovo}>
                    <p>
                        A página não chegou até nós. Costuma ser a conexão de internet ou a rede da
                        empresa bloqueando o acesso.
                    </p>
                </Aviso>
            )
        }

        return (
            <Aviso titulo="Tivemos um problema por aqui" acao={tentarDeNovo}>
                <p>O erro é nosso, não seu. Já estamos sabendo.</p>
            </Aviso>
        )
    }

    const { totals, funnel, employer } = data

    // Sem pedido de crédito ainda não quer dizer página vazia: a consulta de margem vem de outra
    // fonte e pode existir antes do primeiro pedido — é justamente o retrato da folha antes de
    // alguém tomar crédito.
    if (!totals.requests) {
        return (
            <main className="mx-auto max-w-5xl px-4 py-8">
                <Marca />
                <Navegacao />
                <CabecalhoEmpresa employer={employer} window={data.window} />
                <div className="grid gap-4 lg:grid-cols-2">
                    <Secao titulo="Ninguém pediu crédito ainda" larga>
                        <p className="text-sm text-soft">
                            Nenhum colaborador de{" "}
                            <b className="text-ink">{employer.name ?? "sua empresa"}</b> buscou
                            crédito consignado até agora. Assim que o primeiro pedido entrar, esta
                            página passa a mostrar a jornada dele.
                        </p>
                    </Secao>
                    <MargemDaFolha />
                </div>
            </main>
        )
    }

    // A página é do RH, não do nosso funil de vendas. O que a empresa quer saber é como está a folha
    // — quanto da margem já está tomado, quem precisou de crédito, se teve acesso e em que
    // condição, e o que travou quem não teve. Quantos contrataram conosco fica numa nota discreta:
    // para o RH isso é desconto em folha, e o detalhe dele mora na tela de repasse.
    const barrados = funnel.requests - funnel.reached_auction
    const acesso = funnel.reached_auction / funnel.requests

    return (
        <main className="mx-auto max-w-5xl px-4 py-8">
            <Marca />
            <Navegacao />

            <CabecalhoEmpresa employer={employer} window={data.window} />

            <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Kpi
                    rotulo="Colaboradores que buscaram crédito"
                    valor={String(totals.people)}
                    nota={`${totals.requests} pedidos no período`}
                />
                <Kpi
                    rotulo="Margem média disponível"
                    valor={formatCurrency(totals.average_margin)}
                    nota={
                        totals.without_margin
                            ? `${totals.without_margin} sem margem nenhuma`
                            : "ninguém sem margem"
                    }
                    tom={totals.without_margin ? "stop" : "ok"}
                />
                <Kpi
                    rotulo="Pedidos aprovados na análise"
                    valor={formatPercent(acesso)}
                    nota={`${funnel.reached_auction} de ${funnel.requests} foram disputados pelos bancos`}
                />
                <Kpi
                    rotulo="Taxa média das propostas"
                    valor={
                        funnel.average_rate == null
                            ? "—"
                            : `${TAXA.format(funnel.average_rate)}% a.m.`
                    }
                    nota={
                        funnel.average_installments == null
                            ? undefined
                            : `em média ${Math.round(funnel.average_installments)} parcelas`
                    }
                />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                <MargemDaFolha />

                <Secao
                    titulo="Acesso ao crédito"
                    descricao="O que aconteceu com os pedidos dos seus colaboradores"
                >
                    <Barras
                        rotulosLongos
                        linhas={[
                            {
                                rotulo: "Aprovados e disputados pelos bancos",
                                valor: funnel.reached_auction,
                                tom: "dado",
                            },
                            { rotulo: "Barrados na análise", valor: barrados, tom: "stop" },
                        ]}
                    />
                    <p className="mt-4 text-sm text-faint">
                        Quem passa na análise tem o pedido disputado por vários bancos ao mesmo
                        tempo, e decide sozinho se quer seguir.{" "}
                        {funnel.signed > 0 &&
                            `${funnel.signed} ${
                                funnel.signed === 1
                                    ? "contrato foi assinado"
                                    : "contratos foram assinados"
                            } no período; o desconto em folha de cada um aparece em Repasse.`}
                    </p>
                </Secao>

                {data.drops.length > 0 && (
                    <Secao
                        titulo="O que barrou os pedidos"
                        descricao="Alguns motivos, como tempo de casa e cadastro, vêm dos dados da folha"
                    >
                        <Barras
                            rotulosLongos
                            linhas={data.drops.map((item) => ({
                                rotulo: item.reason,
                                valor: item.requests,
                                tom: "stop" as const,
                            }))}
                        />
                    </Secao>
                )}

                <Secao
                    titulo="Pedidos por mês"
                    descricao="Quantos pedidos e quantas pessoas distintas"
                >
                    <Barras
                        linhas={data.by_month.map((mes) => ({
                            rotulo: mesCurto(mes.month),
                            valor: mes.requests,
                            nota: `· ${mes.people} pessoas`,
                        }))}
                    />
                </Secao>

                <Secao
                    titulo="Margem consignável disponível"
                    descricao="Quanto sobra da margem de quem pediu crédito"
                >
                    <Barras
                        linhas={data.distributions.margin.map((faixa, indice) => ({
                            rotulo: faixa.bucket,
                            valor: faixa.people,
                            tom: tomDaMargem(indice, data.distributions.margin.length),
                        }))}
                    />
                </Secao>

                <Secao titulo="Valor pedido" descricao="Último pedido de cada colaborador">
                    <Barras
                        linhas={data.distributions.amount.map((faixa) => ({
                            rotulo: faixa.bucket,
                            valor: faixa.people,
                        }))}
                    />
                </Secao>

                <Secao titulo="Tempo de casa" descricao="Em meses, de quem pediu crédito">
                    <Barras
                        linhas={data.distributions.tenure.map((faixa) => ({
                            rotulo: faixa.bucket,
                            valor: faixa.people,
                        }))}
                    />
                </Secao>

                <Secao titulo="Prazo pedido" descricao="Em parcelas">
                    <Barras
                        linhas={data.distributions.installments.map((faixa) => ({
                            rotulo: faixa.bucket,
                            valor: faixa.people,
                        }))}
                    />
                </Secao>
            </div>
        </main>
    )
}
